import type { LucideIcon } from 'lucide-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
export type ButtonSize = 'sm' | 'md';

// Кнопки: 36 px — основні дії сторінки й діалогів (у тон полям вводу), 30 px — дії в рядках, таблицях, фільтрах.
const base =
  'inline-flex shrink-0 items-center justify-center gap-2 rounded-field border font-semibold whitespace-nowrap select-none ' +
  'transition-[background-color,border-color,color] duration-150 disabled:pointer-events-none aria-disabled:pointer-events-none';

const variants: Record<ButtonVariant, string> = {
  primary:
    'border-transparent bg-primary-solid text-on-primary-solid hover:bg-primary-solid-hover ' +
    'disabled:bg-surface-3 disabled:text-faint aria-disabled:bg-surface-3 aria-disabled:text-faint',
  secondary: 'border-border bg-surface text-fg hover:border-border-strong hover:bg-surface-2 disabled:opacity-55 aria-disabled:opacity-55',
  ghost: 'border-transparent bg-transparent text-fg hover:bg-surface-2 disabled:opacity-55 aria-disabled:opacity-55',
  danger: 'border-transparent bg-danger text-on-danger hover:bg-danger-hover disabled:opacity-55 aria-disabled:opacity-55',
  accent: 'border-transparent bg-accent text-on-accent hover:bg-accent-hover disabled:opacity-55 aria-disabled:opacity-55',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-[30px] px-3 text-[13px]',
  md: 'h-9 px-4 text-[14px]',
};

const iconSizes: Record<ButtonSize, string> = { sm: 'size-[30px]', md: 'size-9' };

export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className?: string) {
  return cn(base, variants[variant], sizes[size], className);
}

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Іконка зліва (lucide). */
  icon?: LucideIcon;
  /** Іконка справа. */
  iconRight?: LucideIcon;
}

/** Кнопка. type за замовчуванням 'button' (у формі — передати type="submit"). */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon: Icon,
  iconRight: IconRight,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const iconCls = size === 'sm' ? 'size-4' : 'size-[18px]';
  return (
    <button
      type={type}
      className={buttonClass(variant, size, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 'xs' : 'sm'} /> : Icon ? <Icon className={iconCls} aria-hidden /> : null}
      {children}
      {IconRight && !loading ? <IconRight className={iconCls} aria-hidden /> : null}
    </button>
  );
}

export interface ButtonLinkProps extends LinkProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
}

/** Посилання (react-router) у вигляді кнопки. */
export function ButtonLink({ variant = 'secondary', size = 'md', icon: Icon, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={buttonClass(variant, size, className)} {...rest}>
      {Icon ? <Icon className={size === 'sm' ? 'size-4' : 'size-[18px]'} aria-hidden /> : null}
      {children as ReactNode}
    </Link>
  );
}

export interface IconButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  /** Обов'язковий доступний підпис (aria-label і title). */
  label: string;
  icon: LucideIcon;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Лічильник-бейдж у куті (напр. непрочитані сповіщення). */
  badge?: number;
  /** Натиснутий стан (перемикачі). */
  pressed?: boolean;
}

/** Кнопка-іконка з обов'язковим підписом. */
export function IconButton({
  label,
  icon: Icon,
  variant = 'ghost',
  size = 'md',
  loading,
  badge,
  pressed,
  className,
  type = 'button',
  disabled,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={disabled || loading}
      className={cn(
        base,
        variants[variant],
        iconSizes[size],
        'relative px-0',
        variant === 'ghost' && 'text-muted hover:bg-surface-3 hover:text-fg',
        pressed && 'bg-primary-soft text-primary-ink hover:bg-primary-soft',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : <Icon className={size === 'sm' ? 'size-4' : 'size-[18px]'} aria-hidden />}
      {badge ? (
        <span className="num absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-[10px] leading-none font-semibold text-on-danger">
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </button>
  );
}
